import { useState } from 'react';
import type { Course, Hole, KeepOutZone, LngLat } from '../lib/types';
import type { Fix } from '../lib/gps';
import { API_BASE } from '../lib/api';
import { distanceM } from '../lib/geo';

interface Props {
  course: Course;
  fix: Fix | null;
  unit: 'm' | 'yd';
}

type Point = 'tee' | 'front' | 'middle' | 'back';

interface Captured {
  tee?: LngLat;
  front?: LngLat;
  middle?: LngLat;
  back?: LngLat;
  par: number;
  strokeIndex: number;
}

const STORE = 'fairway.survey.v1';

/**
 * Survey mode — the one manual step no public dataset can replace.
 *
 * A greenkeeper drives the course once, standing on each tee and walking the
 * green edges, tapping capture. That produces the real coordinates the whole
 * product depends on. Everything is written to localStorage as it goes so a
 * flat battery on the 14th does not cost the morning.
 */
export default function SurveyScreen({ course, fix, unit }: Props) {
  const [holeNo, setHoleNo] = useState(1);
  const [teeColour, setTeeColour] = useState(course.teeColours[0] ?? 'White');
  const [adminKey, setAdminKey] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, Captured>>(() => {
    try {
      return JSON.parse(localStorage.getItem(STORE) ?? '{}');
    } catch {
      return {};
    }
  });

  const key = `${holeNo}`;
  const current: Captured = data[key] ?? { par: 4, strokeIndex: holeNo };

  const save = (next: Record<string, Captured>) => {
    setData(next);
    localStorage.setItem(STORE, JSON.stringify(next));
  };

  const capture = (which: Point) => {
    if (!fix) {
      setStatus('No GPS fix — cannot capture.');
      return;
    }
    if (fix.accuracy > 10) {
      setStatus(`Accuracy is ±${Math.round(fix.accuracy)}m. Wait for ±10m or better before capturing.`);
      return;
    }
    save({ ...data, [key]: { ...current, [which]: fix.pos } });
    setStatus(`${which.toUpperCase()} captured on hole ${holeNo} at ±${Math.round(fix.accuracy)}m.`);
  };

  const complete = Object.entries(data).filter(
    ([, c]) => c.tee && c.front && c.middle && c.back
  ).length;

  const buildCourse = (): Course => {
    const holes: Hole[] = [];
    const keepOut: KeepOutZone[] = [];
    for (let n = 1; n <= 18; n++) {
      const c = data[`${n}`];
      if (!c?.tee || !c.front || !c.middle || !c.back) continue;
      const length = Math.round(distanceM(c.tee, c.middle));
      holes.push({
        number: n,
        par: c.par,
        strokeIndex: c.strokeIndex,
        yardage: { [teeColour]: length },
        tees: { [teeColour]: c.tee, White: c.tee },
        green: { front: c.front, middle: c.middle, back: c.back },
        hazards: [],
      });
      // Green radius from the captured front/back span, plus a 6m collar.
      const radius = Math.max(12, distanceM(c.front, c.back) / 2 + 6);
      keepOut.push({
        id: `ko-green-${n}`,
        label: `Green ${n}`,
        centre: c.middle,
        radius,
        severity: 'block',
      });
      keepOut.push({
        id: `ko-tee-${n}`,
        label: `Tee ${n}`,
        centre: c.tee,
        radius: 15,
        severity: 'warn',
      });
    }
    const centre = holes.length
      ? ([
          holes.reduce((s, h) => s + h.green.middle[0], 0) / holes.length,
          holes.reduce((s, h) => s + h.green.middle[1], 0) / holes.length,
        ] as LngLat)
      : course.centre;

    return {
      id: course.id,
      name: course.name,
      teeColours: [teeColour],
      centre,
      holes,
      keepOut,
      source: 'operator',
    };
  };

  const upload = async () => {
    const built = buildCourse();
    if (!built.holes.length) {
      setStatus('Nothing to upload — capture all four points on at least one hole.');
      return;
    }
    setStatus('Uploading…');
    try {
      const r = await fetch(`${API_BASE}/api/courses/${encodeURIComponent(course.id)}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', 'x-fairway-admin': adminKey },
        body: JSON.stringify(built),
      });
      if (!r.ok) {
        setStatus(`Upload failed (${r.status}). ${r.status === 401 ? 'Check the admin key.' : ''}`);
        return;
      }
      setStatus(`Uploaded ${built.holes.length} surveyed holes. Every buggy picks this up on next launch.`);
    } catch (e) {
      setStatus(`Upload failed: ${(e as Error).message}. Data is still saved on this device.`);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(buildCourse(), null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${course.id}-surveyed.json`;
    a.click();
  };

  const teeToGreen =
    current.tee && current.middle ? Math.round(distanceM(current.tee, current.middle)) : null;

  return (
    <div className="screen">
      <div className="card">
        <h2>SURVEY — HOLE {holeNo}</h2>
        <div className="row" style={{ marginBottom: 10 }}>
          <button className="btn" onClick={() => setHoleNo((n) => Math.max(1, n - 1))}>
            ‹
          </button>
          <span style={{ fontSize: 30, fontWeight: 800, minWidth: 50, textAlign: 'center' }}>
            {holeNo}
          </span>
          <button className="btn" onClick={() => setHoleNo((n) => Math.min(18, n + 1))}>
            ›
          </button>
          <div className="spacer" />
          <span className={`pill ${!fix ? 'bad' : fix.accuracy <= 10 ? 'ok' : 'warn'}`}>
            {fix ? `±${Math.round(fix.accuracy)}m` : 'NO FIX'}
          </span>
          <span className="pill">{complete}/18 COMPLETE</span>
        </div>

        <div className="row" style={{ marginBottom: 10 }}>
          {(['tee', 'front', 'middle', 'back'] as Point[]).map((p) => (
            <button
              key={p}
              className={`btn ${current[p] ? 'primary' : ''}`}
              style={{ flex: 1, minHeight: 76 }}
              onClick={() => capture(p)}
            >
              {p.toUpperCase()}
              <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.8 }}>
                {current[p] ? 'captured — tap to redo' : 'stand here, tap'}
              </div>
            </button>
          ))}
        </div>

        <div className="field">
          <label>Par</label>
          {[3, 4, 5].map((p) => (
            <button
              key={p}
              className={`btn ${current.par === p ? 'primary' : 'ghost'}`}
              onClick={() => save({ ...data, [key]: { ...current, par: p } })}
            >
              {p}
            </button>
          ))}
          <label style={{ minWidth: 90, marginLeft: 12 }}>Stroke index</label>
          <input
            type="number"
            min={1}
            max={18}
            value={current.strokeIndex}
            onChange={(e) =>
              save({ ...data, [key]: { ...current, strokeIndex: Number(e.target.value) } })
            }
          />
        </div>

        {teeToGreen != null && (
          <div className="muted">
            Measured tee to centre: <strong>{unit === 'yd' ? Math.round(teeToGreen / 0.9144) : teeToGreen}{unit}</strong>
            {' '}— compare against the scorecard. More than a few off means one of the points is wrong.
          </div>
        )}
        {status && (
          <div className="muted" style={{ marginTop: 8, color: 'var(--accent)' }}>
            {status}
          </div>
        )}
      </div>

      <div className="card">
        <h2>PUBLISH</h2>
        <div className="field">
          <label>Tee colour</label>
          <select value={teeColour} onChange={(e) => setTeeColour(e.target.value)}>
            {course.teeColours.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Admin key</label>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="x-fairway-admin"
          />
        </div>
        <div className="row">
          <button className="btn primary" onClick={upload} disabled={!complete || !adminKey}>
            Upload {complete} holes
          </button>
          <button className="btn" onClick={exportJson} disabled={!complete}>
            Export JSON
          </button>
          <div className="spacer" />
          <button
            className="btn danger"
            onClick={() => {
              if (confirm('Discard all captured survey points on this device?')) save({});
            }}
          >
            Clear survey
          </button>
        </div>
        <div className="muted" style={{ marginTop: 8 }}>
          Capture on a clear day with the buggy stopped. Stand on the green edge nearest the fairway
          for FRONT and the far edge for BACK — the app derives the green size and the keep-off fence
          from that span.
        </div>
      </div>
    </div>
  );
}
